import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, Skeleton, Snackbar,
  Stack, TextField, Typography,
} from '@mui/material';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import ChampDate from '../ChampDate/ChampDate';

const VIDE = { reference: '', dateClaims: '', adress: '', guarantees_ids: [] };

/** Les natures telles que l'assuré les choisit dans son parcours. */
const NATURES = {
  EAU: "De l'eau", FEU: 'Du feu', VOL: 'Un vol ou une effraction',
  TEMPETE: 'Du vent, de la grêle, la neige', VITRAGE: 'Un bris de vitrage',
  RC: "Un dommage causé à quelqu'un", AUTRE: 'Autre chose',
};

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

/** Le sinistre : ce qui s'est passé, où, et sous quelles garanties. */
function EditClaims() {
  const { expertiseId } = useParams();

  const [sinistre, setSinistre] = useState(VIDE);
  const [initial, setInitial] = useState(VIDE);
  const [garanties, setGaranties] = useState([]);
  const [recit, setRecit] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [enregistre, setEnregistre] = useState(false);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [fiche, liste] = await Promise.all([
        axiosInstance.get(`/claims/${expertiseId}/`),
        axiosInstance.get('/guarantee/'),
      ]);
      const lu = {
        reference: fiche.data.reference || '',
        dateClaims: (fiche.data.dateClaims || '').split('T')[0],
        adress: fiche.data.adress || '',
        guarantees_ids: (fiche.data.guarantees || []).map((g) => g.id),
      };
      setSinistre(lu);
      setInitial(lu);
      setGaranties(liste.data.results || liste.data);
      setRecit({
        declaration: fiche.data.declaration || '',
        nature: fiche.data.nature || '',
        declare_le: fiche.data.declare_le || '',
      });
      setErreur('');
    } catch {
      setErreur("Les données du sinistre n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  const modifier = (champ) => (e) => {
    setSinistre((etat) => ({ ...etat, [champ]: e.target.value }));
    if (erreur) setErreur('');
  };

  const basculerGarantie = (id) => {
    setSinistre((etat) => ({
      ...etat,
      guarantees_ids: etat.guarantees_ids.includes(id)
        ? etat.guarantees_ids.filter((autre) => autre !== id)
        : [...etat.guarantees_ids, id],
    }));
  };

  const modifie = JSON.stringify(sinistre) !== JSON.stringify(initial);

  const soumettre = async (e) => {
    e.preventDefault();
    setOccupe(true);
    setErreur('');
    try {
      // PATCH : la déclaration de l'assuré n'est pas éditée ici, et un PUT
      // la renverrait telle qu'on l'a lue — au risque d'écraser une version
      // plus récente.
      await axiosInstance.patch(`/claims/${expertiseId}/`, {
        reference: sinistre.reference,
        dateClaims: sinistre.dateClaims || null,
        adress: sinistre.adress,
        guarantees_ids: sinistre.guarantees_ids,
      });
      setInitial(sinistre);
      setEnregistre(true);
    } catch {
      setErreur("Les modifications n'ont pas pu être enregistrées.");
    } finally {
      setOccupe(false);
    }
  };

  if (chargement) {
    return <Stack spacing={2}>
      <Skeleton height={56} /><Skeleton height={56} /><Skeleton height={160} />
    </Stack>;
  }

  return (
    <Box component="form" onSubmit={soumettre} noValidate>
      <Stack spacing={3}>
        {erreur && <Alert severity="error">{erreur}</Alert>}

        {/* En lecture seule, et volontairement : ces mots sont ceux de
            l'assuré et font foi face à la compagnie. Quand le récit est
            incomplet, ce qu'il faut est une précision de sa part, pas une
            phrase écrite à sa place. */}
        <Fiche
          titre="Ce que l'assuré a déclaré"
          explication={[
            recit?.nature && (NATURES[recit.nature] || recit.nature),
            dateCourte(recit?.declare_le) && `déclaré le ${dateCourte(recit.declare_le)}`,
          ].filter(Boolean).join(' — ') || undefined}
        >
          {recit?.declaration ? (
            <Typography variant="body1"
                        sx={{ whiteSpace: 'pre-wrap',
                              bgcolor: 'background.default',
                              border: '1px solid', borderColor: 'divider',
                              borderRadius: 1, p: 2 }}>
              {recit.declaration}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Rien n'a encore été déclaré pour ce dossier.
            </Typography>
          )}
        </Fiche>

        <Fiche titre="Le sinistre">
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Référence de la compagnie"
                value={sinistre.reference}
                onChange={modifier('reference')}
                disabled={occupe}
                helperText="Telle que la compagnie l'a attribuée."
              />
              <ChampDate
                label="Date du sinistre"
                value={sinistre.dateClaims}
                onChange={modifier('dateClaims')}
                disabled={occupe}
                InputLabelProps={{ shrink: true }}
                helperText=" "
              />
            </Stack>

            <TextField
              label="Adresse du sinistre"
              value={sinistre.adress}
              onChange={modifier('adress')}
              disabled={occupe}
              helperText="Le lieu du dommage, s'il diffère de celui de l'assuré."
            />
          </Stack>
        </Fiche>

        <Fiche
          titre="Garanties"
          explication="Celles que le contrat mobilise pour ce sinistre."
          actions={sinistre.guarantees_ids.length > 0 && (
            <Chip size="small" color="primary"
                  label={`${sinistre.guarantees_ids.length} retenue${
                    sinistre.guarantees_ids.length > 1 ? 's' : ''}`} />
          )}
        >
          {garanties.length === 0 ? (
            <Alert severity="info">
              Aucune garantie n'est encore définie au référentiel. Vous pouvez
              enregistrer le reste du sinistre sans elles.
            </Alert>
          ) : (
            <Box sx={{ display: 'grid', gap: 0.5,
                       gridTemplateColumns: {
                         xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
              {garanties.map((garantie) => (
                <FormControlLabel
                  key={garantie.id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={sinistre.guarantees_ids.includes(garantie.id)}
                      onChange={() => basculerGarantie(garantie.id)}
                      disabled={occupe}
                    />
                  )}
                  label={garantie.name}
                />
              ))}
            </Box>
          )}
        </Fiche>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button onClick={() => setSinistre(initial)}
                  disabled={occupe || !modifie}>
            Annuler les modifications
          </Button>
          {/* L'ancien écran interdisait d'enregistrer quand le référentiel
              de garanties était vide : le sinistre devenait insaisissable. */}
          <Button type="submit" variant="contained"
                  disabled={occupe || !modifie}>
            {occupe ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Stack>
      </Stack>

      <Snackbar
        open={enregistre}
        autoHideDuration={3000}
        onClose={() => setEnregistre(false)}
        message="Sinistre enregistré"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default EditClaims;
